/* eslint-disable jsx-a11y/anchor-is-valid */

import {faEye, faEyeSlash, faKey, faEnvelope, faLock} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {useContext, useEffect, useState, useRef,} from 'react'
import {Link, useNavigate, useParams, useSearchParams} from 'react-router-dom'
import * as Yup from 'yup'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {useAuth} from '../core/Auth'
import {announceAuthSessionLogin, setupAxios} from '../core/AuthHelpers'
import {login} from '../core/_requests'
import roleContext from '../../../context/roleContext'
import {useThemeMode} from '../../../../_metronic/partials'
import {ThemeModeComponent} from '../../../../_metronic/assets/ts/layout'
import {getFeaturesByUserId} from '../../../../api/AdminAPI'
import {checkSetup} from '../../../../api/SetupAPI'
import {DarkModeContext} from '../AuthPage'

const eye = <FontAwesomeIcon icon={faEye} />
const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Please enter a email'),

  password: Yup.string()
    .min(1, 'Minimum 1 symbols')
    .max(50, 'Maximum 50 letters')
    .required('Please enter a Password'),
})

const initialValues = {
  id: '',
  email: '',
  password: '',
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function Login() {
  const {mode} = useThemeMode()
  const isDarkMode = useContext(DarkModeContext)
  const [loading, setLoading] = useState(false)
  const {saveAuth, setCurrentUser} = useAuth()

  const navigate = useNavigate()
  let roleState: any = useContext(roleContext)
  const [passwordShown, setPasswordShown] = useState(false)
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login')
  const weekendDropdownRef = useRef<HTMLDivElement>(null)
  const [weekendDropdownOpen, setWeekendDropdownOpen] = useState(false)
  const [selectedWeekends, setSelectedWeekends] = useState<string[]>([])
  const [weekStartDropdownOpen, setWeekStartDropdownOpen] = useState(false)
  const [selectedWeekStartDay, setSelectedWeekStartDay] = useState('')
  const weekStartDropdownRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        weekendDropdownRef.current &&
        !weekendDropdownRef.current.contains(event.target as Node)
      ) {
        setWeekendDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        weekStartDropdownRef.current &&
        !weekStartDropdownRef.current.contains(event.target as Node)
      ) {
        setWeekStartDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const togglePasswordVisiblity = () => {
    setPasswordShown((prev) => !prev)
  }

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      await checkSetup().then(async (res1) => {
        if (res1?.statusCode === 200) {
          if (res1?.data?.setupCompleted) {
            await login(values.email.trim(), values.password.trim()).then((res) => {
              setSubmitting(false)
              setLoading(false)
              if (res.statusCode == 200) {
                announceAuthSessionLogin(res.data.id)

                // Check if this is a first-time login
                if (res.data.isFirstLogin && !res.data.isPasswordChanged) {
                  // Save auth data for first-time login
                  saveAuth({...res.data})
                  setCurrentUser({...res.data})
                  sessionStorage.setItem('isFirstLogin', 'true')
                  sessionStorage.setItem('isLoggedIn', 'true')
                  
                  // Redirect to password reset for first-time users with URL parameters
                  const resetUrl = `/reset-password?email=${encodeURIComponent(res.data.email)}&token=${encodeURIComponent(res.data.token)}&isFirstLogin=true`
                  navigate(resetUrl)
                  return
                }

                // Regular login flow for users who have already reset their password
                saveAuth({...res.data})
                let features: string[] = []
                getFeaturesByUserId(res?.data?.id).then((res2) => {
                  if (res2.statusCode === 200) {
                    const userRole = res2?.data[0]?.role;
                    const roleName = userRole?.name;
                    const normalizedRoleName = roleName
                      ?.trim()
                      .toUpperCase()
                      .replace(/_/g, ' ');
                    res2?.data[0]?.role?.permissions?.map(
                      (feature: {permission: {name: string; id: string}}) => {
                        features.push(feature?.permission?.name)
                      }
                    )
                    
                    // Enhanced role validation and feature assignment
                    // if (roleName === 'Super Admin') {
                    if (normalizedRoleName === 'SUPER ADMIN') {
                      // Add all possible features for Super Admin as a fallback
                      const allPossibleFeatures = [
                        'dashboard', 'users_registration', 'roles', 'holidays', 'leave_management_system',
                        'attendance_management_system', 'asset_management_system', 'user_personal_details',
                        'bank_details', 'deactivate_account', 'general_config', 'leave_config', 'designations',
                        'employment_types', 'leave_types', 'leave_policies', 'leave_audit', 'job_posting',
                        'hiring_management', 'payroll_management', 'reports', 'policies', 'organization',
                        'projects', 'tasks', 'notifications', 'settings', 'backup', 'logs', 'analytics'
                      ];
                      
                      // Add any missing features
                      allPossibleFeatures.forEach(feature => {
                        ['view', 'edit', 'create', 'delete'].forEach(action => {
                          const fullFeature = `${feature}_${action}`;
                          if (!features.includes(fullFeature)) {
                            features.push(fullFeature);
                          }
                        });
                        if (!features.includes(feature)) {
                          features.push(feature);
                        }
                      });
                    } else {
                      // Validate features for non-Super Admin roles
                      const validateRoleFeatures = (role: string, currentFeatures: string[]) => {
                        const rolePermissionMap = {
                          'Employee': ['dashboard', 'attendance_management_system', 'leave_management_system', 'holidays'],
                          'Admin': ['dashboard', 'attendance_management_system', 'leave_management_system', 'holidays', 
                                   'users_registration', 'user_personal_details', 'general_config', 'roles', 'designations',
                                   'employment_types', 'leave_config', 'leave_types', 'leave_policies', 'leave_audit', 
                                   'reports', 'policies', 'configuration']
                        };
                        
                        const allowedFeatures = rolePermissionMap[role as keyof typeof rolePermissionMap];
                        if (allowedFeatures) {
                          // Filter out any unauthorized features
                          const validFeatures = currentFeatures.filter(f => allowedFeatures.includes(f));
                          
                          if (validFeatures.length !== currentFeatures.length) {
                            console.warn(`Invalid features detected for ${role} role. Expected: ${allowedFeatures.length}, Got: ${currentFeatures.length}`);
                          }
                          
                          return validFeatures;
                        }
                        
                        return currentFeatures;
                      };
                      
                      // Apply validation
                      features = validateRoleFeatures(roleName, features);
                    }
                    
                    roleState.updateState(features, roleName)
                    sessionStorage.setItem(
                      'currentUserRole',
                      JSON.stringify({
                        roleName: roleName,
                        features: features,
                      })
                    )

                    sessionStorage.setItem('isLoggedIn', 'true')
                    setCurrentUser({...res.data})
                    setSubmitting(false)
                    
                    // Always redirect to dashboard as primary preference
                    if (features.includes('dashboard')) {
                      navigate(`/dashboard`)
                    } else if (features.includes('attendance_management_system')) {
                      navigate(`/ams`)
                    } else if (features.includes('leave_management_system')) {
                      navigate(`/leave`)
                    } else if (features.includes('user_personal_details')) {
                      navigate(`/profile`)
                    } else {
                      // Fallback - force redirect to dashboard even if not in features
                      navigate(`/dashboard`)
                    }
                  } else {
                    setStatus(res.message)
                    saveAuth(undefined)
                    setSubmitting(false)
                    setLoading(false)
                  }
                })
                // }
                // sessionStorage.setItem('isLoggedIn', 'true')
              } else if (res.statusCode == 406) {
                setStatus(res.message)
                saveAuth(undefined)
                setSubmitting(false)
                setLoading(false)
                navigate(`/resetPassword`, {
                  state: {userId: res.data.id, tenantId: res.data.tenant},
                })
              } else {
                setStatus(res.message)
                saveAuth(undefined)
                setSubmitting(false)
                setLoading(false)
              }
            })
          } else {
            setStatus('No registration found. Please first complete sign up process.')
            saveAuth(undefined)
            setSubmitting(false)
            setLoading(false)
          }
        } else {
          setStatus(res1.message)
          saveAuth(undefined)
          setSubmitting(false)
          setLoading(false)
        }
      })
    },
  })

  useEffect(() => {
    let mounted = true
    if ((mounted = true)) {
    }
    return () => {
      mounted = false
    }
  }, [])

  return (
    <>
    {/* Logo */}
    <div
      style={{
        width: '100%',
        height: '58px',
        marginBottom: '2px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 0,
      }}
    >
      <img
        src={toAbsoluteUrl(isDarkMode ? '/media/logos/whitesialogo.png' : '/media/logos/sialogo.png')}
        alt='OfficeSIA'
        style={{
          width: '240px',
          height: '6000px',
          objectFit: 'contain',
          display: 'block',
        }}
      />
    </div>

    {/* Tab Navigation */}
    <div
      style={{
        display: 'flex',
        width: '100%',
        marginBottom: '8px',
        borderBottom: '1px solid #E2E8F0',
      }}
    >
      <button
        onClick={() => setActiveTab('login')}
        style={{
          flex: 1,
          padding: '6px 12px',
          background: 'transparent',
          border: 'none',
          borderBottom:
            activeTab === 'login'
              ? '3px solid #3B82D0'
              : '3px solid transparent',
          color: activeTab === 'login' ? '#3B82D0' : '#64748B',
          fontSize: '0.9rem',
          fontWeight: activeTab === 'login' ? '600' : '500',
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
      >
        Sign In
      </button>

      {/* <button
        onClick={() => setActiveTab('signup')}
        style={{
          flex: 1,
          padding: '6px 12px',
          background: 'transparent',
          border: 'none',
          borderBottom:
            activeTab === 'signup'
              ? '3px solid #3B82D0'
              : '3px solid transparent',
          color: activeTab === 'signup' ? '#3B82D0' : '#64748B',
          fontSize: '0.9rem',
          fontWeight: activeTab === 'signup' ? '600' : '500',
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
      >
        Sign Up
      </button> */}
    </div>

      {/* Login Form */}
      {activeTab === 'login' && (
        <form
          className='form w-100'
          onSubmit={formik.handleSubmit}
          noValidate
          id='kt_login_signin_form'
          style={{display: 'flex', flexDirection: 'column', gap: '20px'}}
        >
          {/* begin::Heading */}
          <div style={{textAlign: 'center', marginBottom: '4px'}}>
            <h2 style={{fontSize: '1.5rem', fontWeight: '700', color: isDarkMode ? '#F8FAFC' : '#102A56', marginBottom: '4px', margin: 0}}>
              Welcome Back
            </h2>
            <p style={{fontSize: '0.9rem', color: isDarkMode ? '#CBD5E1' : '#64748B', margin: '4px 0 0 0', lineHeight: '1.3'}}>
              Sign in to continue to OfficeSIA
            </p>
          </div>
          
          {/* Scrollable Form Container */}
          <div style={{maxHeight: '280px', overflowY: 'auto', paddingRight: '8px', scrollbarWidth: 'thin', scrollbarColor: '#CBD5E1 #F1F5F9', width: '100%', boxSizing: 'border-box'}}>
            {/* Custom scrollbar styling for WebKit browsers */}
            <style>
              {`
                .login-form-container::-webkit-scrollbar {
                  width: 6px;
                }
                .login-form-container::-webkit-scrollbar-track {
                  background: #F1F5F9;
                  border-radius: 3px;
                }
                .login-form-container::-webkit-scrollbar-thumb {
                  background: #CBD5E1;
                  border-radius: 3px;
                }
                .login-form-container::-webkit-scrollbar-thumb:hover {
                  background: #94A3B8;
                }
              `}
            </style>
            
            <div className='login-form-container' style={{display: 'flex', flexDirection: 'column', gap: '16px', paddingBottom: '8px', width: '100%', boxSizing: 'border-box', minWidth: 0}}>
          
          {formik.status && (
            <div style={{textAlign: 'center', marginBottom: '8px', padding: '10px 16px', background: '#FEF2F2', borderRadius: '8px', border: '1px solid #FECACA'}}>
              <p style={{color: '#DC2626', margin: 0, fontSize: '0.85rem'}}>{formik.status}</p>
            </div>
          )}
          
          {/* begin::Form group Email */}
          <div style={{marginBottom: '2px', minWidth: 0}}>
            <label style={{display: 'block', fontSize: '0.8rem', fontWeight: '600', color: isDarkMode ? '#F8FAFC' : '#102A56', marginBottom: '4px'}}>
              Email
            </label>
            <div style={{position: 'relative'}}>
              <div style={{position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: isDarkMode ? '#94A3B8' : '#64748B', zIndex: 1}}>
                <FontAwesomeIcon icon={faEnvelope} style={{fontSize: '0.85rem'}} />
              </div>
              <input
                {...formik.getFieldProps('email')}
                className={clsx(
                  'form-control',
                  {'is-invalid': formik.touched.email && formik.errors.email},
                  {'is-valid': formik.touched.email && !formik.errors.email}
                )}
                type='email'
                name='email'
                autoComplete='off'
                placeholder='Enter your email'
                onChange={(e) => formik.setFieldValue('email', e.target.value.toLowerCase())}
                style={{
                  padding: '10px 12px 10px 38px',
                  borderRadius: '6px',
                  border: formik.touched.email && formik.errors.email ? '1px solid #DC2626' : isDarkMode ? '1px solid #334155' : '1px solid #E2E8F0',
                  fontSize: '0.9rem',
                  background: isDarkMode ? '#0F172A' : '#F8FAFC',
                  color: isDarkMode ? '#F8FAFC' : '#1E293B',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  minWidth: 0,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3B82D0'
                  e.target.style.background = isDarkMode ? '#1E293B' : '#FFFFFF'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = formik.touched.email && formik.errors.email ? '#DC2626' : isDarkMode ? '#334155' : '#E2E8F0'
                  e.target.style.background = isDarkMode ? '#0F172A' : '#F8FAFC'
                }}
              />
            </div>
            {formik.touched.email && formik.errors.email && (
              <div style={{marginTop: '2px'}}>
                <span style={{color: '#DC2626', fontSize: '0.75rem'}}>{formik.errors.email}</span>
              </div>
            )}
          </div>
          {/* end::Form group Email */}

          {/* begin::Form group Password */}
          <div style={{marginBottom: '2px', minWidth: 0}}>
            <label style={{display: 'block', fontSize: '0.8rem', fontWeight: '600', color: isDarkMode ? '#F8FAFC' : '#102A56', marginBottom: '4px'}}>
              Password
            </label>
            <div style={{position: 'relative'}}>
              <div style={{position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: isDarkMode ? '#94A3B8' : '#64748B', zIndex: 1}}>
                <FontAwesomeIcon icon={faLock} style={{fontSize: '0.85rem'}} />
              </div>
              <input
                placeholder='Enter your password'
                type={passwordShown ? 'text' : 'password'}
                autoComplete='off'
                {...formik.getFieldProps('password')}
                className={clsx(
                  'form-control',
                  {'is-invalid': formik.touched.password && formik.errors.password},
                  {'is-valid': formik.touched.password && !formik.errors.password}
                )}
                style={{
                  padding: '10px 38px 10px 38px',
                  borderRadius: '6px',
                  border: formik.touched.password && formik.errors.password ? '1px solid #DC2626' : isDarkMode ? '1px solid #334155' : '1px solid #E2E8F0',
                  fontSize: '0.9rem',
                  background: isDarkMode ? '#0F172A' : '#F8FAFC',
                  color: isDarkMode ? '#F8FAFC' : '#1E293B',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  minWidth: 0,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3B82D0'
                  e.target.style.background = isDarkMode ? '#1E293B' : '#FFFFFF'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = formik.touched.password && formik.errors.password ? '#DC2626' : isDarkMode ? '#334155' : '#E2E8F0'
                  e.target.style.background = isDarkMode ? '#0F172A' : '#F8FAFC'
                }}
              />
              <div
                style={{position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: isDarkMode ? '#94A3B8' : '#64748B', cursor: 'pointer', zIndex: 1, transition: 'color 0.2s'}}
                onClick={togglePasswordVisiblity}
                onMouseEnter={(e) => e.currentTarget.style.color = '#3B82D0'}
                onMouseLeave={(e) => e.currentTarget.style.color = isDarkMode ? '#94A3B8' : '#64748B'}
              >
                {passwordShown ? (
                  <FontAwesomeIcon icon={faEyeSlash} style={{fontSize: '0.85rem'}} />
                ) : (
                  <FontAwesomeIcon icon={faEye} style={{fontSize: '0.85rem'}} />
                )}
              </div>
            </div>
            {formik.touched.password && formik.errors.password && (
              <div style={{marginTop: '2px'}}>
                <span style={{color: '#DC2626', fontSize: '0.75rem'}}>{formik.errors.password}</span>
              </div>
            )}
          </div>
          {/* end::Form group Password */}

          {/* begin::Wrapper - Remember me & Forgot password */}
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
              <input
                type='checkbox'
                id='rememberMe'
                style={{width: '14px', height: '14px', cursor: 'pointer', accentColor: '#3B82D0'}}
              />
              <label htmlFor='rememberMe' style={{fontSize: '0.8rem', color: isDarkMode ? '#CBD5E1' : '#64748B', cursor: 'pointer', margin: 0}}>
                Keep me signed in
              </label>
            </div>
            <Link 
              to='/forgotpassword' 
              style={{fontSize: '0.8rem', color: '#3B82D0', textDecoration: 'none', fontWeight: '500', transition: 'color 0.2s'}}
              onMouseEnter={(e) => e.currentTarget.style.color = '#2563EB'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#3B82D0'}
            >
              Forgot Password?
            </Link>
          </div>
          {/* end::Wrapper */}
            </div> {/* End of scrollable container */}
          </div>

          {/* begin::Action */}
          <div style={{marginTop: '2px'}}>
            <button
              type='submit'
              id='kt_sign_in_submit'
              disabled={formik.isSubmitting || !formik.isValid}
              style={{
                width: '100%',
                padding: '10px 20px',
                borderRadius: '6px',
                background: formik.isSubmitting || !formik.isValid 
                  ? '#94A3B8' 
                  : 'linear-gradient(135deg, #3B82D0 0%, #2563EB 100%)',
                color: '#FFFFFF',
                fontSize: '0.9rem',
                fontWeight: '600',
                border: 'none',
                cursor: formik.isSubmitting || !formik.isValid ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '6px',
                boxShadow: formik.isSubmitting || !formik.isValid 
                  ? 'none' 
                  : '0 4px 12px rgba(59, 130, 208, 0.25)',
              }}
              onMouseEnter={(e) => {
                if (!formik.isSubmitting && formik.isValid) {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)'
                  e.currentTarget.style.transform = 'translateY(-1px)'
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 208, 0.35)'
                }
              }}
              onMouseLeave={(e) => {
                if (!formik.isSubmitting && formik.isValid) {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #3B82D0 0%, #2563EB 100%)'
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 208, 0.25)'
                }
              }}
            >
              {!loading && (
                <span style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                  Sign In 
                  <FontAwesomeIcon icon={faKey} style={{fontSize: '0.8rem'}} />
                </span>
              )}
              {loading && (
                <span style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                  Please wait...
                  <span className='spinner-border spinner-border-sm align-middle' style={{width: '0.8rem', height: '0.8rem'}}></span>
                </span>
              )}
            </button>
          </div>
          
          {/* begin::Signup link */}
          {/* <div style={{textAlign: 'center', marginTop: '12px'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px'}}>
              <div style={{flex: 1, height: '1px', background: '#E2E8F0'}}></div>
              <span style={{fontSize: '0.8rem', color: '#94A3B8', fontWeight: '500'}}>or</span>
              <div style={{flex: 1, height: '1px', background: '#E2E8F0'}}></div>
            </div>
            <p style={{fontSize: '0.85rem', color: isDarkMode ? '#CBD5E1' : '#64748B', margin: 0}}>
              Don't have an account?{' '}
              <button
                onClick={() => setActiveTab('signup')}
                style={{background: 'none', border: 'none', color: '#3B82D0', fontWeight: '600', cursor: 'pointer', padding: 0, transition: 'color 0.2s', fontSize: '0.85rem'}}
                onMouseEnter={(e) => e.currentTarget.style.color = '#2563EB'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#3B82D0'}
              >
                Signup
              </button>
            </p>
          </div> */}
          {/* end::Action */}
        </form>
      )}

      {/* Signup Form Container */}
      {activeTab === 'signup' && (
        <div style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
          <div style={{textAlign: 'center', marginBottom: '4px'}}>
            <h2 style={{fontSize: '1.5rem', fontWeight: '700', color: isDarkMode ? '#F8FAFC' : '#102A56', marginBottom: '4px', margin: 0}}>
              Create Account
            </h2>
            <p style={{fontSize: '0.9rem', color: isDarkMode ? '#CBD5E1' : '#64748B', margin: '4px 0 0 0', lineHeight: '1.3'}}>
              Sign up to get started with OfficeSIA
            </p>
          </div>
          
          {/* Scrollable Form Container */}
          <div style={{maxHeight: '280px', overflowY: 'auto', paddingRight: '8px', scrollbarWidth: 'thin', scrollbarColor: '#CBD5E1 #F1F5F9', width: '100%', boxSizing: 'border-box'}}>
            {/* Custom scrollbar styling for WebKit browsers */}
            <style>
              {`
                .signup-form-container::-webkit-scrollbar {
                  width: 6px;
                }
                .signup-form-container::-webkit-scrollbar-track {
                  background: #F1F5F9;
                  border-radius: 3px;
                }
                .signup-form-container::-webkit-scrollbar-thumb {
                  background: #CBD5E1;
                  border-radius: 3px;
                }
                .signup-form-container::-webkit-scrollbar-thumb:hover {
                  background: #94A3B8;
                }
              `}
            </style>
            
            {/* Registration Form Fields */}
            <div className='signup-form-container' style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px', paddingBottom: '8px', width: '100%', boxSizing: 'border-box', minWidth: 0}}>
            {/* Company Name */}
            <div style={{marginBottom: '2px', minWidth: 0}}>
              <label style={{display: 'block', fontSize: '0.8rem', fontWeight: '600', color: isDarkMode ? '#F8FAFC' : '#102A56', marginBottom: '4px'}}>
                Company Name <span style={{color: '#DC2626'}}>*</span>
              </label>
              <input
                type='text'
                name='companyName'
                placeholder='Enter company name'
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: isDarkMode ? '1px solid #334155' : '1px solid #E2E8F0',
                  fontSize: '0.9rem',
                  background: isDarkMode ? '#0F172A' : '#F8FAFC',
                  color: isDarkMode ? '#F8FAFC' : '#1E293B',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  minWidth: 0,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3B82D0'
                  e.target.style.background = '#FFFFFF'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#E2E8F0'
                  e.target.style.background = '#F8FAFC'
                }}
              />
            </div>

            {/* Address */}
            <div style={{marginBottom: '4px', minWidth: 0}}>
              <label style={{display: 'block', fontSize: '0.85rem', fontWeight: '600', color: isDarkMode ? '#F8FAFC' : '#102A56', marginBottom: '6px'}}>
                Address <span style={{color: '#DC2626'}}>*</span>
              </label>
              <input
                type='text'
                name='address'
                placeholder='Enter address'
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: isDarkMode ? '1px solid #334155' : '1px solid #E2E8F0',
                  fontSize: '0.95rem',
                  background: isDarkMode ? '#0F172A' : '#F8FAFC',
                  color: isDarkMode ? '#F8FAFC' : '#1E293B',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  minWidth: 0,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3B82D0'
                  e.target.style.background = isDarkMode ? '#1E293B' : '#FFFFFF'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = isDarkMode ? '#334155' : '#E2E8F0'
                  e.target.style.background = isDarkMode ? '#0F172A' : '#F8FAFC'
                }}
              />
            </div>

            {/* Country */}
            <div style={{marginBottom: '4px', minWidth: 0}}>
              <label style={{display: 'block', fontSize: '0.85rem', fontWeight: '600', color: isDarkMode ? '#F8FAFC' : '#102A56', marginBottom: '6px'}}>
                Country <span style={{color: '#DC2626'}}>*</span>
              </label>
              <input
                type='text'
                name='country'
                placeholder='Enter country'
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: isDarkMode ? '1px solid #334155' : '1px solid #E2E8F0',
                  fontSize: '0.95rem',
                  background: isDarkMode ? '#0F172A' : '#F8FAFC',
                  color: isDarkMode ? '#F8FAFC' : '#1E293B',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  minWidth: 0,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3B82D0'
                  e.target.style.background = isDarkMode ? '#1E293B' : '#FFFFFF'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = isDarkMode ? '#334155' : '#E2E8F0'
                  e.target.style.background = isDarkMode ? '#0F172A' : '#F8FAFC'
                }}
              />
            </div>

            {/* State */}
            <div style={{marginBottom: '4px', minWidth: 0}}>
              <label style={{display: 'block', fontSize: '0.85rem', fontWeight: '600', color: isDarkMode ? '#F8FAFC' : '#102A56', marginBottom: '6px'}}>
                State <span style={{color: '#DC2626'}}>*</span>
              </label>
              <input
                type='text'
                name='state'
                placeholder='Enter state'
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: isDarkMode ? '1px solid #334155' : '1px solid #E2E8F0',
                  fontSize: '0.95rem',
                  background: isDarkMode ? '#0F172A' : '#F8FAFC',
                  color: isDarkMode ? '#F8FAFC' : '#1E293B',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  minWidth: 0,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3B82D0'
                  e.target.style.background = isDarkMode ? '#1E293B' : '#FFFFFF'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = isDarkMode ? '#334155' : '#E2E8F0'
                  e.target.style.background = isDarkMode ? '#0F172A' : '#F8FAFC'
                }}
              />
            </div>

            {/* City */}
            <div style={{marginBottom: '4px', minWidth: 0}}>
              <label style={{display: 'block', fontSize: '0.85rem', fontWeight: '600', color: isDarkMode ? '#F8FAFC' : '#102A56', marginBottom: '6px'}}>
                City <span style={{color: '#DC2626'}}>*</span>
              </label>
              <input
                type='text'
                name='city'
                placeholder='Enter city'
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: isDarkMode ? '1px solid #334155' : '1px solid #E2E8F0',
                  fontSize: '0.95rem',
                  background: isDarkMode ? '#0F172A' : '#F8FAFC',
                  color: isDarkMode ? '#F8FAFC' : '#1E293B',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  minWidth: 0,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3B82D0'
                  e.target.style.background = isDarkMode ? '#1E293B' : '#FFFFFF'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = isDarkMode ? '#334155' : '#E2E8F0'
                  e.target.style.background = isDarkMode ? '#0F172A' : '#F8FAFC'
                }}
              />
            </div>

            {/* PIN Code */}
            <div style={{marginBottom: '4px', minWidth: 0}}>
              <label style={{display: 'block', fontSize: '0.85rem', fontWeight: '600', color: isDarkMode ? '#F8FAFC' : '#102A56', marginBottom: '6px'}}>
                PIN Code <span style={{color: '#DC2626'}}>*</span>
              </label>
              <input
                type='number'
                name='pincode'
                placeholder='Enter PIN code'
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: isDarkMode ? '1px solid #334155' : '1px solid #E2E8F0',
                  fontSize: '0.95rem',
                  background: isDarkMode ? '#0F172A' : '#F8FAFC',
                  color: isDarkMode ? '#F8FAFC' : '#1E293B',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  minWidth: 0,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3B82D0'
                  e.target.style.background = isDarkMode ? '#1E293B' : '#FFFFFF'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = isDarkMode ? '#334155' : '#E2E8F0'
                  e.target.style.background = isDarkMode ? '#0F172A' : '#F8FAFC'
                }}
              />
            </div>

            {/* Website */}
            <div style={{marginBottom: '4px', gridColumn: 'span 2', minWidth: 0}}>
              <label style={{display: 'block', fontSize: '0.85rem', fontWeight: '600', color: isDarkMode ? '#F8FAFC' : '#102A56', marginBottom: '6px'}}>
                Website <span style={{color: '#DC2626'}}>*</span>
              </label>
              <input
                type='text'
                name='website'
                placeholder='Enter website URL'
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: isDarkMode ? '1px solid #334155' : '1px solid #E2E8F0',
                  fontSize: '0.95rem',
                  background: isDarkMode ? '#0F172A' : '#F8FAFC',
                  color: isDarkMode ? '#F8FAFC' : '#1E293B',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  minWidth: 0,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3B82D0'
                  e.target.style.background = isDarkMode ? '#1E293B' : '#FFFFFF'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = isDarkMode ? '#334155' : '#E2E8F0'
                  e.target.style.background = isDarkMode ? '#0F172A' : '#F8FAFC'
                }}
              />
            </div>

            {/* Financial Year Start Date */}
            <div style={{marginBottom: '4px', minWidth: 0}}>
              <label style={{display: 'block', fontSize: '0.85rem', fontWeight: '600', color: isDarkMode ? '#F8FAFC' : '#102A56', marginBottom: '6px'}}>
                Financial Year Start Date <span style={{color: '#DC2626'}}>*</span>
              </label>
              <input
                type='date'
                name='financialYearStartDate'
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: isDarkMode ? '1px solid #334155' : '1px solid #E2E8F0',
                  fontSize: '0.95rem',
                  background: isDarkMode ? '#0F172A' : '#F8FAFC',
                  color: isDarkMode ? '#F8FAFC' : '#1E293B',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  minWidth: 0,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3B82D0'
                  e.target.style.background = isDarkMode ? '#1E293B' : '#FFFFFF'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = isDarkMode ? '#334155' : '#E2E8F0'
                  e.target.style.background = isDarkMode ? '#0F172A' : '#F8FAFC'
                }}
              />
            </div>

            {/* Financial Year End Date */}
            <div style={{marginBottom: '4px', minWidth: 0}}>
              <label style={{display: 'block', fontSize: '0.85rem', fontWeight: '600', color: isDarkMode ? '#F8FAFC' : '#102A56', marginBottom: '6px'}}>
                Financial Year End Date <span style={{color: '#DC2626'}}>*</span>
              </label>
              <input
                type='date'
                name='financialYearEndDate'
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: isDarkMode ? '1px solid #334155' : '1px solid #E2E8F0',
                  fontSize: '0.95rem',
                  background: isDarkMode ? '#0F172A' : '#F8FAFC',
                  color: isDarkMode ? '#F8FAFC' : '#1E293B',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  minWidth: 0,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3B82D0'
                  e.target.style.background = isDarkMode ? '#1E293B' : '#FFFFFF'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = isDarkMode ? '#334155' : '#E2E8F0'
                  e.target.style.background = isDarkMode ? '#0F172A' : '#F8FAFC'
                }}
              />
            </div>

            {/* Weekends */}
            <div ref={weekendDropdownRef} style={{marginBottom: '4px', minWidth: 0, position: 'relative'}}>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  color: isDarkMode ? '#F8FAFC' : '#102A56',
                  marginBottom: '6px',
                }}
              >
                Weekends <span style={{color: '#DC2626'}}>*</span>
              </label>

              {/* Dropdown Button */}
              <div
                onClick={() => setWeekendDropdownOpen((prev) => !prev)}
                style={{
                  width: '100%',
                  minHeight: '44px',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: weekendDropdownOpen
                    ? '1px solid #3B82D0'
                    : '1px solid #E2E8F0',
                  background: isDarkMode ? '#0F172A' : '#F8FAFC',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  boxSizing: 'border-box',
                  transition: 'all 0.2s ease',
                }}
              >
                <span
                  style={{
                    color: selectedWeekends.length > 0 ? '#334155' : '#94A3B8',
                    fontSize: '0.9rem',
                  }}
                >
                  {selectedWeekends.length > 0
                    ? selectedWeekends
                        .map(
                          (day) =>
                            day.charAt(0).toUpperCase() + day.slice(1)
                        )
                        .join(', ')
                    : 'Select weekend days'}
                </span>

                <span
                  style={{
                    color: isDarkMode ? '#CBD5E1' : '#64748B',
                    fontSize: '12px',
                    transform: weekendDropdownOpen
                      ? 'rotate(180deg)'
                      : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                >
                  ▼
                </span>
              </div>

              {/* Dropdown Menu */}
              {weekendDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '4px',
                    background: isDarkMode ? '#1E293B' : '#FFFFFF',
                    border: isDarkMode ? '1px solid #334155' : '1px solid #E2E8F0',
                    borderRadius: '8px',
                    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.12)',
                    zIndex: 1000,
                    overflow: 'hidden',
                  }}
                >
                  {/* Scrollable Days */}
                  <div
                    style={{
                      maxHeight: '180px',
                      overflowY: 'auto',
                      padding: '6px',
                      scrollbarWidth: 'thin',
                      scrollbarColor: isDarkMode ? '#475569 #1E293B' : '#CBD5E1 #F8FAFC',
                    }}
                  >
                    <style>
                      {`
                        .weekend-dropdown-scroll::-webkit-scrollbar {
                          width: 6px;
                        }

                        .weekend-dropdown-scroll::-webkit-scrollbar-track {
                          background: ${isDarkMode ? '#1E293B' : '#F8FAFC'};
                          border-radius: 4px;
                        }

                        .weekend-dropdown-scroll::-webkit-scrollbar-thumb {
                          background: ${isDarkMode ? '#475569' : '#CBD5E1'};
                          border-radius: 4px;
                        }

                        .weekend-dropdown-scroll::-webkit-scrollbar-thumb:hover {
                          background: ${isDarkMode ? '#64748B' : '#94A3B8'};
                        }
                      `}
                    </style>

                    <div className='weekend-dropdown-scroll'>
                      {[
                        {label: 'Monday', value: 'monday'},
                        {label: 'Tuesday', value: 'tuesday'},
                        {label: 'Wednesday', value: 'wednesday'},
                        {label: 'Thursday', value: 'thursday'},
                        {label: 'Friday', value: 'friday'},
                        {label: 'Saturday', value: 'saturday'},
                        {label: 'Sunday', value: 'sunday'},
                      ].map((day) => {
                        const isSelected = selectedWeekends.includes(day.value)

                        return (
                          <label
                            key={day.value}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '10px 12px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              background: isSelected
                                ? (isDarkMode ? '#1E3A5F' : '#EAF4FF')
                                : 'transparent',
                              color: isSelected
                                ? '#2563EB'
                                : (isDarkMode ? '#CBD5E1' : '#475569'),
                              fontSize: '0.85rem',
                              fontWeight: isSelected ? '600' : '500',
                              transition: 'all 0.15s ease',
                            }}
                          >
                            <input
                              type='checkbox'
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedWeekends((prev) => [
                                    ...prev,
                                    day.value,
                                  ])
                                } else {
                                  setSelectedWeekends((prev) =>
                                    prev.filter(
                                      (selected) => selected !== day.value
                                    )
                                  )
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                width: '16px',
                                height: '16px',
                                cursor: 'pointer',
                                accentColor: '#3B82D0',
                              }}
                            />

                            <span>{day.label}</span>
                          </label>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Week Start Day */}
            <div
              ref={weekStartDropdownRef}
              style={{
                marginBottom: '4px',
                minWidth: 0,
                position: 'relative',
              }}
            >
              <label
                style={{
                  display: 'block',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  color: isDarkMode ? '#F8FAFC' : '#102A56',
                  marginBottom: '6px',
                }}
              >
                Week Start Day <span style={{color: '#DC2626'}}>*</span>
              </label>

              {/* Dropdown Button */}
              <div
                onClick={() =>
                  setWeekStartDropdownOpen((prev) => !prev)
                }
                style={{
                  width: '100%',
                  minHeight: '44px',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: weekStartDropdownOpen
                    ? '1px solid #3B82D0'
                    : '1px solid #E2E8F0',
                  background: isDarkMode ? '#0F172A' : '#F8FAFC',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  boxSizing: 'border-box',
                  transition: 'all 0.2s ease',
                }}
              >
                <span
                  style={{
                    color: selectedWeekStartDay
                      ? '#334155'
                      : '#94A3B8',
                    fontSize: '0.9rem',
                  }}
                >
                  {selectedWeekStartDay
                    ? selectedWeekStartDay.charAt(0).toUpperCase() +
                      selectedWeekStartDay.slice(1)
                    : 'Select week start day'}
                </span>

                <span
                  style={{
                    color: isDarkMode ? '#CBD5E1' : '#64748B',
                    fontSize: '12px',
                    transform: weekStartDropdownOpen
                      ? 'rotate(180deg)'
                      : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                >
                  ▼
                </span>
              </div>

              {/* Dropdown Menu */}
              {weekStartDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '4px',
                    background: isDarkMode ? '#1E293B' : '#FFFFFF',
                    border: isDarkMode ? '1px solid #334155' : '1px solid #E2E8F0',
                    borderRadius: '8px',
                    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.12)',
                    zIndex: 1000,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      maxHeight: '180px',
                      overflowY: 'auto',
                      padding: '6px',
                      scrollbarWidth: 'thin',
                      scrollbarColor: isDarkMode ? '#475569 #1E293B' : '#CBD5E1 #F8FAFC',
                    }}
                  >
                    {[
                      'monday',
                      'tuesday',
                      'wednesday',
                      'thursday',
                      'friday',
                      'saturday',
                      'sunday',
                    ].map((day) => {
                      const isSelected = selectedWeekStartDay === day

                      return (
                        <div
                          key={day}
                          onClick={() => {
                            setSelectedWeekStartDay(day)
                            setWeekStartDropdownOpen(false)
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 12px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            background: isSelected
                              ? (isDarkMode ? '#1E3A5F' : '#EAF4FF')
                              : 'transparent',
                            color: isSelected
                              ? '#2563EB'
                              : (isDarkMode ? '#CBD5E1' : '#475569'),
                            fontSize: '0.85rem',
                            fontWeight: isSelected ? '600' : '500',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          {/* Radio Circle */}
                          <span
                            style={{
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              border: isSelected
                                ? '5px solid #3B82D0'
                                : '1.5px solid #CBD5E1',
                              background: isDarkMode ? '#1E293B' : '#FFFFFF',
                              boxSizing: 'border-box',
                              flexShrink: 0,
                            }}
                          />

                          {day.charAt(0).toUpperCase() + day.slice(1)}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
            {/* First Name */}
            <div style={{marginBottom: '4px', minWidth: 0}}>
              <label style={{display: 'block', fontSize: '0.85rem', fontWeight: '600', color: isDarkMode ? '#F8FAFC' : '#102A56', marginBottom: '6px'}}>
                First Name <span style={{color: '#DC2626'}}>*</span>
              </label>
              <input
                type='text'
                name='firstName'
                placeholder='Enter first name'
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: isDarkMode ? '1px solid #334155' : '1px solid #E2E8F0',
                  fontSize: '0.95rem',
                  background: isDarkMode ? '#0F172A' : '#F8FAFC',
                  color: isDarkMode ? '#F8FAFC' : '#1E293B',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  minWidth: 0,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3B82D0'
                  e.target.style.background = isDarkMode ? '#1E293B' : '#FFFFFF'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = isDarkMode ? '#334155' : '#E2E8F0'
                  e.target.style.background = isDarkMode ? '#0F172A' : '#F8FAFC'
                }}
              />
            </div>

            {/* Last Name */}
            <div style={{marginBottom: '4px', minWidth: 0}}>
              <label style={{display: 'block', fontSize: '0.85rem', fontWeight: '600', color: isDarkMode ? '#F8FAFC' : '#102A56', marginBottom: '6px'}}>
                Last Name <span style={{color: '#DC2626'}}>*</span>
              </label>
              <input
                type='text'
                name='lastName'
                placeholder='Enter last name'
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: isDarkMode ? '1px solid #334155' : '1px solid #E2E8F0',
                  fontSize: '0.95rem',
                  background: isDarkMode ? '#0F172A' : '#F8FAFC',
                  color: isDarkMode ? '#F8FAFC' : '#1E293B',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  minWidth: 0,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3B82D0'
                  e.target.style.background = isDarkMode ? '#1E293B' : '#FFFFFF'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = isDarkMode ? '#334155' : '#E2E8F0'
                  e.target.style.background = isDarkMode ? '#0F172A' : '#F8FAFC'
                }}
              />
            </div>

            {/* Email */}
            <div style={{marginBottom: '4px', minWidth: 0}}>
              <label style={{display: 'block', fontSize: '0.85rem', fontWeight: '600', color: isDarkMode ? '#F8FAFC' : '#102A56', marginBottom: '6px'}}>
                Email <span style={{color: '#DC2626'}}>*</span>
              </label>
              <input
                type='text'
                name='email'
                placeholder='Enter email'
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: isDarkMode ? '1px solid #334155' : '1px solid #E2E8F0',
                  fontSize: '0.95rem',
                  background: isDarkMode ? '#0F172A' : '#F8FAFC',
                  color: isDarkMode ? '#F8FAFC' : '#1E293B',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  minWidth: 0,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3B82D0'
                  e.target.style.background = isDarkMode ? '#1E293B' : '#FFFFFF'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = isDarkMode ? '#334155' : '#E2E8F0'
                  e.target.style.background = isDarkMode ? '#0F172A' : '#F8FAFC'
                }}
              />
            </div>

            {/* Password */}
            <div style={{marginBottom: '4px', minWidth: 0}}>
              <label style={{display: 'block', fontSize: '0.85rem', fontWeight: '600', color: isDarkMode ? '#F8FAFC' : '#102A56', marginBottom: '6px'}}>
                Password <span style={{color: '#DC2626'}}>*</span>
              </label>
              <input
                type='text'
                name='password'
                placeholder='Enter password'
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: isDarkMode ? '1px solid #334155' : '1px solid #E2E8F0',
                  fontSize: '0.95rem',
                  background: isDarkMode ? '#0F172A' : '#F8FAFC',
                  color: isDarkMode ? '#F8FAFC' : '#1E293B',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  minWidth: 0,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3B82D0'
                  e.target.style.background = isDarkMode ? '#1E293B' : '#FFFFFF'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = isDarkMode ? '#334155' : '#E2E8F0'
                  e.target.style.background = isDarkMode ? '#0F172A' : '#F8FAFC'
                }}
              />
            </div>

            {/* Mobile */}
            <div style={{marginBottom: '4px', minWidth: 0}}>
              <label style={{display: 'block', fontSize: '0.85rem', fontWeight: '600', color: isDarkMode ? '#F8FAFC' : '#102A56', marginBottom: '6px'}}>
                Mobile <span style={{color: '#DC2626'}}>*</span>
              </label>
              <input
                type='number'
                name='mobile'
                placeholder='Enter mobile number'
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: isDarkMode ? '1px solid #334155' : '1px solid #E2E8F0',
                  fontSize: '0.95rem',
                  background: isDarkMode ? '#0F172A' : '#F8FAFC',
                  color: isDarkMode ? '#F8FAFC' : '#1E293B',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  minWidth: 0,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3B82D0'
                  e.target.style.background = isDarkMode ? '#1E293B' : '#FFFFFF'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = isDarkMode ? '#334155' : '#E2E8F0'
                  e.target.style.background = isDarkMode ? '#0F172A' : '#F8FAFC'
                }}
              />
            </div>

            {/* In Time */}
            <div style={{marginBottom: '4px', minWidth: 0}}>
              <label style={{display: 'block', fontSize: '0.85rem', fontWeight: '600', color: isDarkMode ? '#F8FAFC' : '#102A56', marginBottom: '6px'}}>
                In Time <span style={{color: '#DC2626'}}>*</span>
              </label>
              <input
                type='time'
                name='inTime'
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: isDarkMode ? '1px solid #334155' : '1px solid #E2E8F0',
                  fontSize: '0.95rem',
                  background: isDarkMode ? '#0F172A' : '#F8FAFC',
                  color: isDarkMode ? '#F8FAFC' : '#1E293B',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  minWidth: 0,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3B82D0'
                  e.target.style.background = isDarkMode ? '#1E293B' : '#FFFFFF'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = isDarkMode ? '#334155' : '#E2E8F0'
                  e.target.style.background = isDarkMode ? '#0F172A' : '#F8FAFC'
                }}
              />
            </div>

            {/* Out Time */}
            <div style={{marginBottom: '4px', minWidth: 0}}>
              <label style={{display: 'block', fontSize: '0.85rem', fontWeight: '600', color: isDarkMode ? '#F8FAFC' : '#102A56', marginBottom: '6px'}}>
                Out Time <span style={{color: '#DC2626'}}>*</span>
              </label>
              <input
                type='time'
                name='outTime'
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: isDarkMode ? '1px solid #334155' : '1px solid #E2E8F0',
                  fontSize: '0.95rem',
                  background: isDarkMode ? '#0F172A' : '#F8FAFC',
                  color: isDarkMode ? '#F8FAFC' : '#1E293B',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  minWidth: 0,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3B82D0'
                  e.target.style.background = isDarkMode ? '#1E293B' : '#FFFFFF'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = isDarkMode ? '#334155' : '#E2E8F0'
                  e.target.style.background = isDarkMode ? '#0F172A' : '#F8FAFC'
                }}
              />
            </div>
            </div> {/* End of form fields grid */}
          </div> {/* End of scrollable container */}

          {/* Action Buttons */}
          <div style={{display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '2px'}}>
            <button
              type='button'
              style={{
                padding: '10px 20px',
                borderRadius: '6px',
                background: isDarkMode ? '#0F172A' : '#F8FAFC',
                color: isDarkMode ? '#CBD5E1' : '#64748B',
                fontSize: '0.9rem',
                fontWeight: '600',
                border: '1px solid #E2E8F0',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#E2E8F0'
                e.currentTarget.style.borderColor = '#CBD5E1'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#F8FAFC'
                e.currentTarget.style.borderColor = '#E2E8F0'
              }}
            >
              Back
            </button>
            <button
              type='button'
              style={{
                padding: '10px 20px',
                borderRadius: '6px',
                background: 'linear-gradient(135deg, #3B82D0 0%, #2563EB 100%)',
                color: '#FFFFFF',
                fontSize: '0.9rem',
                fontWeight: '600',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(59, 130, 208, 0.25)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)'
                e.currentTarget.style.transform = 'translateY(-1px)'
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 208, 0.35)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #3B82D0 0%, #2563EB 100%)'
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 208, 0.25)'
              }}
            >
              Submit
            </button>
          </div>
          
          <div style={{textAlign: 'center', marginTop: '12px'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px'}}>
              <div style={{flex: 1, height: '1px', background: '#E2E8F0'}}></div>
              <span style={{fontSize: '0.8rem', color: '#94A3B8', fontWeight: '500'}}>or</span>
              <div style={{flex: 1, height: '1px', background: '#E2E8F0'}}></div>
            </div>
            <p style={{fontSize: '0.85rem', color: isDarkMode ? '#CBD5E1' : '#64748B', margin: 0}}>
              Already have an account?{' '}
              <button
                onClick={() => setActiveTab('login')}
                style={{background: 'none', border: 'none', color: '#3B82D0', fontWeight: '600', cursor: 'pointer', padding: 0, transition: 'color 0.2s', fontSize: '0.85rem'}}
                onMouseEnter={(e) => e.currentTarget.style.color = '#2563EB'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#3B82D0'}
              >
                Login
              </button>
            </p>
          </div>
        </div>
      )}
    </>
  )
}
